import { z } from "zod";
import { BOOK_SORTS, type BookListDto } from "./catalog";
import { bookListFullQuerySchema } from "./ratings";

/*
 * Shelves contract shared by server and UI: limits, schemas (issue messages are field
 * error codes translated by the client) and the DTOs of the shelves API.
 */

export const SHELF_NAME_MAX = 100;
export const SHELF_DESCRIPTION_MAX = 2_000;
export const SHELF_MAX_PER_USER = 500;
export const SHELF_MAX_BOOKS = 10_000;
export const SHELF_BULK_MAX = 500;

export const SHELF_VISIBILITIES = ["private", "shared"] as const;
export type ShelfVisibility = (typeof SHELF_VISIBILITIES)[number];

export const SHELF_KINDS = ["manual", "smart"] as const;
export type ShelfKind = (typeof SHELF_KINDS)[number];

export const SHELF_SORTS = ["position", "added", "title", "author"] as const;
export type ShelfSort = (typeof SHELF_SORTS)[number];

/** Sorts of a shelf's contents: the shelf sorts plus every catalog sort (deduplicated). */
export const SHELF_BOOK_SORTS = [...new Set<string>([...SHELF_SORTS, ...BOOK_SORTS])] as [
  ShelfSort | (typeof BOOK_SORTS)[number],
  ...(ShelfSort | (typeof BOOK_SORTS)[number])[],
];
export type ShelfBookSort = (typeof SHELF_BOOK_SORTS)[number];

/** Case-insensitive, diacritics-sensitive uniqueness key of a shelf name. */
export function shelfNameKey(name: string): string {
  return name.trim().toLocaleLowerCase("und");
}

const shelfName = z
  .string({ error: "required" })
  .trim()
  .min(1, { error: "required" })
  .max(SHELF_NAME_MAX, { error: "too_long" });

const shelfDescription = z
  .string({ error: "invalid" })
  .trim()
  .max(SHELF_DESCRIPTION_MAX, { error: "too_long" })
  .nullish()
  .transform((value) => (value ? value : null));

const visibility = z.enum(SHELF_VISIBILITIES, { error: "invalid" });

/** The catalog's list parameters a smart shelf stores (design D1). */
export const SMART_CRITERIA_FILTER_KEYS = [
  "author",
  "series",
  "tag",
  "publisher",
  "language",
  "format",
  "myStatus",
  "myRating",
] as const;

export const smartShelfCriteriaSchema = bookListFullQuerySchema
  .pick({
    q: true,
    author: true,
    series: true,
    tag: true,
    publisher: true,
    language: true,
    format: true,
    myStatus: true,
    myRating: true,
    sort: true,
    order: true,
  })
  .transform((value) => {
    const { q, ...rest } = value;
    // Empty filter arrays are no filter (they would match the whole catalog).
    for (const key of SMART_CRITERIA_FILTER_KEYS) {
      if (Array.isArray(rest[key]) && (rest[key] as unknown[]).length === 0) delete rest[key];
    }
    const trimmed = q?.trim();
    return (trimmed ? { ...rest, q: trimmed } : rest) as typeof rest & { q?: string };
  })
  .refine(
    (value) =>
      Boolean((value as { q?: string }).q) ||
      SMART_CRITERIA_FILTER_KEYS.some(
        (key) => (value as Record<string, unknown>)[key] !== undefined,
      ),
    { error: "required" },
  );
export type SmartShelfCriteria = z.output<typeof smartShelfCriteriaSchema>;

export const shelfCreateSchema = z.preprocess(
  (value) =>
    value && typeof value === "object" && !("kind" in value) ? { ...value, kind: "manual" } : value,
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("manual"),
      name: shelfName,
      description: shelfDescription.optional(),
      visibility: visibility.default("private"),
    }),
    z.object({
      kind: z.literal("smart"),
      name: shelfName,
      description: shelfDescription.optional(),
      visibility: visibility.default("private"),
      criteria: smartShelfCriteriaSchema,
    }),
  ]),
);
export type ShelfCreateInput = z.input<typeof shelfCreateSchema>;
export type ShelfCreateParsed = z.output<typeof shelfCreateSchema>;

export const shelfUpdateSchema = z
  .object({
    name: shelfName.optional(),
    description: shelfDescription.optional(),
    visibility: visibility.optional(),
    criteria: smartShelfCriteriaSchema.optional(),
    // The kind is immutable: any value is rejected.
    kind: z.never({ error: "invalid" }).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { error: "required", path: ["name"] });
export type ShelfUpdateInput = z.input<typeof shelfUpdateSchema>;

const bookId = z.string({ error: "invalid" }).min(1, { error: "invalid" }).max(64, {
  error: "invalid",
});

export const shelfBooksSchema = z.object({
  bookIds: z
    .array(bookId, { error: "invalid" })
    .min(1, { error: "required" })
    .max(SHELF_BULK_MAX, { error: "too_many" }),
});
export type ShelfBooksInput = z.input<typeof shelfBooksSchema>;

export const shelfMoveSchema = z.object({
  position: z
    .number({ error: "invalid" })
    .int({ error: "invalid" })
    .min(0, { error: "out_of_range" })
    // Targets beyond the shelf are clamped by the server.
    .transform((value) => Math.min(value, SHELF_MAX_BOOKS)),
});

/** Shelf contents: the catalog's query (search, filters) with the shelf sorts added. */
export const shelfBooksQuerySchema = bookListFullQuerySchema.extend({
  sort: z.enum(SHELF_BOOK_SORTS, { error: "invalid" }).optional(),
});
export type ShelfBooksQuery = z.infer<typeof shelfBooksQuerySchema>;

export interface ShelfCoverRefDto {
  bookId: string;
  title: string;
  authorName: string | null;
  coverVersion: number | null;
}

export interface ShelfSummaryDto {
  id: string;
  kind: ShelfKind;
  /** Only for smart shelves. */
  criteria: SmartShelfCriteria | null;
  name: string;
  description: string | null;
  visibility: ShelfVisibility;
  bookCount: number;
  covers: ShelfCoverRefDto[];
  updatedAt: string;
  owner: { id: string; name: string };
}

export interface ShelfListDto {
  mine: ShelfSummaryDto[];
  shared: ShelfSummaryDto[];
}

export interface ShelfDetailDto extends ShelfSummaryDto {
  isOwner: boolean;
  createdAt: string;
}

export interface ShelfBooksDto extends BookListDto {
  sort: ShelfBookSort;
  order: "asc" | "desc";
}

export interface ShelfAddResultDto {
  added: number;
  alreadyPresent: number;
}

export interface BookShelvesDto {
  shelfIds: string[];
}
